import { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GoldText from './GoldText'
import { TasteOfGoldGoldLabel } from './TasteOfGoldPromoStyles'
import Table from 'LuxKit/Tables/Table'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import TextButton from 'LuxKit/Button/TextButton'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { TASTE_OF_GOLD_TRANSFER_IMAGE_ID } from '../constants/tasteOfGoldConstants'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'

type Tab = 'offer' | 'about'

const HeroContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    min-height: ${rem(240)};
  }
`

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(8)};
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.control.black100};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};

  ${mediaQueryUp.tablet} {
    flex: 1;
    padding: ${rem(24)} ${rem(20)};
  }
`

const HeroImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  height: ${rem(132)};

  ${mediaQueryUp.tablet} {
    height: auto;
    flex: 1;
  }

  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(24)};
  height: ${rem(24)};
  border-radius: 50%;
  background-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
  color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: ${rem(16)};
`

const ContentWrapper = styled.div`
  padding: ${rem(10)} 0;

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

const TabsWrapper = styled.div`
  padding: ${rem(0)} ${rem(20)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(12)} ${rem(24)} 0;
  }
`

const StyledModalContent = styled(ModalContent)`
  padding-top: ${rem(16)};
  padding-bottom: ${rem(30)};
`

const TermsList = styled.ul`
  margin: 0;
  padding: 0;
  padding-inline-start: ${rem(6)};
  list-style: none;
`

const TermsItem = styled.li`
  padding-inline-start: ${rem(16)};
  margin-bottom: ${rem(2)};
  position: relative;

  &::before {
    content: "\\2022";
    position: absolute;
    inset-inline-start: 0;
    top: 0;
  }
`

const YouAreHereBadge = styled.div`
  position: absolute;
  top: ${rem(-10)};
  inset-inline-start: ${rem(62)};
  transform: translateX(var(--you-are-here-badge-inline-offset));
  display: flex;
  height: ${rem(24)};
  padding: 0 ${rem(8)};
  align-items: center;
  gap: ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.S};
  border: 1px solid ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
  background: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: ${rem(16)};
  --you-are-here-badge-inline-offset: -50%;

  &:dir(rtl) {
    --you-are-here-badge-inline-offset: 50%;
  }
`

const HighlightedTierCard = styled.div`
  position: relative;
  border: 2px solid ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
  border-radius: ${rem(4)};
`

const TierCard = styled.div`
  border-radius: ${rem(4)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const TierBadge = styled(BodyText)`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  flex-shrink: 0;
  width: ${rem(100)};
  border-radius: ${props => props.theme.borderRadius.S};

  ${mediaQueryUp.tablet} {
    width: ${rem(124)};
  }

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const TierCardContent = styled.div`
  display: flex;
  padding: ${rem(12)} ${rem(20)} ${rem(12)} ${rem(2)};
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(2)};
  flex: 1 0 0;
`

interface Props {
  onNavigateAway?: () => void
}

function TasteOfGoldLearnMoreContent(props: Props) {
  const { onNavigateAway } = props
  const intl = useIntl()
  const [activeTab, setActiveTab] = useState<Tab>('offer')
  const handleOfferTab = useCallback(() => setActiveTab('offer'), [])
  const handleAboutTab = useCallback(() => setActiveTab('about'), [])

  const program = getLoyaltyProgramDisplayName()
  const tier = getLoyaltyTierDisplayName('gold')

  const specialGiftLabel = intl.formatMessage({
    id: 'E9xUJz1l5yT0aMejvmaS',
    defaultMessage: 'A Special Gift For You',
    description: 'Gold label heading in Taste of Gold learn more modal hero section',
  })
  const heroDescription = intl.formatMessage({
    id: '_XNMPYFwIVIaXWamF6C6',
    defaultMessage: 'Free airport transfer for your next international escape.',
    description: 'Hero body text in Taste of Gold learn more modal in the site takeover promotion',
  })
  const altText = intl.formatMessage({
    id: 'A_qnSpCYU9xq19rENuGR',
    defaultMessage: 'Airport chauffeur service',
    description: 'Alt text for airport transfer hero image in Taste of Gold learn more modal',
  })
  const limitedTimeOfferTabLabel = intl.formatMessage({
    id: '6vRkqwGSgQMoK0YF8jGP',
    defaultMessage: 'Limited time offer',
    description: 'Tab label for limited time offer section in Taste of Gold learn more modal',
  })
  const aboutTabLabel = intl.formatMessage(
    {
      id: 'b39-ynFcTCi-_2VstZCW',
      defaultMessage: 'About {program}',
      description: 'Tab label for about loyalty program section in Taste of Gold learn more modal',
    },
    { program },
  )

  return <>
    <HeroContainer>
      <HeroCopy>
        <div>
          <TasteOfGoldGoldLabel>{specialGiftLabel}</TasteOfGoldGoldLabel>
        </div>
        <Heading variant="heading2" colour="dark-contrast">
          <GoldText>
            <FormattedMessage
              id="Qd-14NvgomKZ4tZ-6H8Y"
              defaultMessage="Experience <italic>{program} {tier}</italic>"
              description="Hero heading for Taste of Gold learn more modal in the site takeover promotion"
              values={{
                program,
                tier,
                italic: (chunks) => <i>{chunks}</i>,
              }}
            />
          </GoldText>
        </Heading>
        <BodyText variant="large" colour="dark-contrast">
          {heroDescription}
        </BodyText>
      </HeroCopy>
      <HeroImageWrapper>
        <ResponsiveImage
          id={TASTE_OF_GOLD_TRANSFER_IMAGE_ID}
          alt={altText}
          fit="center"
          width="560px"
          height="240px"
        />
      </HeroImageWrapper>
    </HeroContainer>
    <ContentWrapper>
      <TabsWrapper>
        <TabButtonsGroup fit="grow">
          <TabButton size="medium" isActive={activeTab === 'offer'} onClick={handleOfferTab}>
            {limitedTimeOfferTabLabel}
          </TabButton>
          <TabButton size="medium" isActive={activeTab === 'about'} onClick={handleAboutTab}>
            {aboutTabLabel}
          </TabButton>
        </TabButtonsGroup>
      </TabsWrapper>
      <StyledModalContent>
        {activeTab === 'offer' && <LimitedTimeOfferTab />}
        {activeTab === 'about' && <AboutSocieteTab onNavigateAway={onNavigateAway} />}
      </StyledModalContent>
    </ContentWrapper>
  </>
}

function LimitedTimeOfferTab() {
  const intl = useIntl()
  const formatAudCurrency = useCurrencyFormatter({ currency: 'AUD' })
  const minimumSpend = formatAudCurrency(1500, 'roundedDollar')
  const program = getLoyaltyProgramDisplayName()
  const tier = getLoyaltyTierDisplayName('gold')
  const bronze = getLoyaltyTierDisplayName('bronze')

  const step1 = intl.formatMessage(
    {
      id: 'SylZERWlFy-9HALxnd5_',
      defaultMessage: 'Book an international hotel or tour offer (min. {minimumSpend}) between 17 Feb–3 Mar 2026.',
      description: 'Step 1: booking requirement for Taste of Gold limited time offer',
    },
    { minimumSpend },
  )
  const step2 = intl.formatMessage({
    id: 'lRbnr0cw3kpCUYMuF2_e',
    defaultMessage: 'Your complimentary airport transfer will be available at checkout',
    description: 'Step 2: transfer availability for Taste of Gold limited time offer',
  })
  const step3 = intl.formatMessage({
    id: 'bqQ_sqXUjGo8IK-EsnYj',
    defaultMessage: 'Redeem your transfer and travel in Gold style',
    description: 'Step 3: transfer redemption for Taste of Gold limited time offer',
  })
  const steps = [step1, step2, step3]

  const term1 = intl.formatMessage(
    {
      id: 'XwK1uDqlebyQEkxJjYhX',
      defaultMessage: 'Invitation-only offer for select Australian {program} {bronze} members',
      description: 'Terms: eligibility criteria for Taste of Gold offer',
    },
    { program, bronze },
  )
  const term2 = intl.formatMessage(
    {
      id: '75tE1KWTJIHHtYDjVvEL',
      defaultMessage: 'Book an eligible international hotel or tour offer (min. {minimumSpend}) between 17 Feb–3 Mar 2026',
      description: 'Terms: booking requirement for Taste of Gold offer',
    },
    { minimumSpend },
  )
  const term3 = intl.formatMessage({
    id: 'DiNvcl0vmxWgNc9IdxCF',
    defaultMessage: 'Receive 1 complimentary airport transfer, redeemable at checkout',
    description: 'Terms: transfer redemption details for Taste of Gold offer',
  })
  const term4 = intl.formatMessage({
    id: 'NoBzsjrvy6g7IXYGh0cz',
    defaultMessage: 'Transfer is one-time, non-transferable, and not redeemable for cash',
    description: 'Terms: transfer restrictions for Taste of Gold offer',
  })
  const term5 = intl.formatMessage(
    {
      id: '3_RR8zrj4VjMrRcC03pn',
      defaultMessage: 'Offer does not include {tier} status or other {tier} benefits',
      description: 'Terms: scope limitation of Taste of Gold offer in the site takeover promotion',
    },
    { tier },
  )
  const terms = [term1, term2, term3, term4, term5]

  const termsAndConditionsLabel = intl.formatMessage({
    id: 'HnaiGzISbGPuhcqAwWmk',
    defaultMessage: 'Terms and conditions',
    description: 'Terms and conditions heading in Taste of Gold limited time offer tab',
  })

  return (
    <Group direction="vertical" gap={16}>
      <BodyText variant="medium">
        <FormattedMessage
          id="NUovdy67cdie8gFH4JHi"
          defaultMessage="As a {program} {bronze} member, you’ve unlocked a {program} {tier} experience. For a limited time, enjoy a free airport transfer for your next international escape."
          description="Introductory text explaining the Taste of Gold offer for Bronze members"
          values={{
            program,
            bronze,
            tier,
          }}
        />
      </BodyText>

      <Group direction="vertical" gap={8}>
        {steps.map((step, index) => (
          <Group key={step} direction="horizontal" gap={8} verticalAlign="center">
            <StepNumber>{index + 1}</StepNumber>
            <BodyText variant="medium">{step}</BodyText>
          </Group>
        ))}
      </Group>

      <Group direction="vertical" gap={8}>
        <BodyText variant="medium" weight="bold">{termsAndConditionsLabel}</BodyText>
        <TermsList>
          {terms.map(term => (
            <TermsItem key={term}>
              <BodyText variant="small">{term}</BodyText>
            </TermsItem>
          ))}
        </TermsList>
      </Group>
    </Group>
  )
}

interface AboutSocieteTabProps {
  onNavigateAway?: () => void
}

function AboutSocieteTab(props: AboutSocieteTabProps) {
  const { onNavigateAway } = props
  const intl = useIntl()
  const bronzeName = getLoyaltyTierDisplayName('bronze')
  const silverName = getLoyaltyTierDisplayName('silver')
  const goldName = getLoyaltyTierDisplayName('gold')
  const platinumName = getLoyaltyTierDisplayName('platinum')

  const discoverHeading = intl.formatMessage({
    id: 'QiXYlA3skz3Qj0xUWV4d',
    defaultMessage: 'Discover a world of rewards, one tier at a time',
    description: 'Heading for tier overview section in Taste of Gold about tab',
  })
  const unlockDescription = intl.formatMessage({
    id: '2r131nGYglk7hgpCWRDe',
    defaultMessage: 'Unlock bigger and better travel benefits as you move from Bronze to Platinum.',
    description: 'Description for tier overview section in Taste of Gold about tab',
  })
  const youAreHereLabel = intl.formatMessage({
    id: 'Y6uxwkoacZ2etTvcML8p',
    defaultMessage: 'You are here',
    description: 'Badge label indicating current tier in Taste of Gold about tab',
  })
  const bronzeTitle = intl.formatMessage({
    id: 'W2_kcWfgirnxCNPqvNE5',
    defaultMessage: 'Your journey starts here',
    description: 'Bronze tier card title in Taste of Gold about tab element',
  })
  const bronzeDesc = intl.formatMessage({
    id: 'qT292hlbCN0HYOSNMrQ1',
    defaultMessage: 'Start earning points and status credits from your very first booking. Use points to pay less on your next escape.',
    description: 'Bronze tier card description in Taste of Gold about tab',
  })
  const silverTitle = intl.formatMessage({
    id: 'XZ5xx8-9Jk_DKiAcS7-c',
    defaultMessage: 'Step up the benefits',
    description: 'Silver tier card title in Taste of Gold about tab element',
  })
  const silverDesc = intl.formatMessage({
    id: 'fiQ1ce2uIdq-QvNw_rHz',
    defaultMessage: 'Access added perks like free upgrades, lounge passes, and tour credits.',
    description: 'Silver tier card description in Taste of Gold about tab',
  })
  const goldTitle = intl.formatMessage({
    id: 'M97kKt1XB5QNAsxkaWB7',
    defaultMessage: 'A new Gold standard',
    description: 'Gold tier card title in Taste of Gold about tab in the site takeover promotion',
  })
  const goldDesc = intl.formatMessage({
    id: 'yyfTxu-KWInHHngQZge_',
    defaultMessage: 'Enjoy elevated benefits including chauffeur airport transfers and multiple free upgrades.',
    description: 'Gold tier card description in Taste of Gold about tab',
  })
  const platinumTitle = intl.formatMessage({
    id: 'NgfGzUyLkcsfpHGx3t5i',
    defaultMessage: 'The complete VIP experience',
    description: 'Platinum tier card title in Taste of Gold about tab',
  })
  const platinumDesc = intl.formatMessage({
    id: 'XHI51ZqrTAHXDZawejpv',
    defaultMessage: 'Indulge in unlimited chauffeur transfers and additional room upgrades for the ultimate escape.',
    description: 'Platinum tier card description in Taste of Gold about tab',
  })
  const tierInfo = [
    { tier: 'bronze' as const, title: bronzeTitle, description: bronzeDesc, highlighted: true },
    { tier: 'silver' as const, title: silverTitle, description: silverDesc, highlighted: false },
    { tier: 'gold' as const, title: goldTitle, description: goldDesc, highlighted: false },
    { tier: 'platinum' as const, title: platinumTitle, description: platinumDesc, highlighted: false },
  ]

  const movingUpHeading = intl.formatMessage({
    id: '_At3T1UIczDhkVI3UcGb',
    defaultMessage: 'Moving up through the tiers',
    description: 'Heading for tier progression section in Taste of Gold about tab',
  })
  const statusCreditsLabel = intl.formatMessage({
    id: 'Y1e8sXuCIpRhVaz02cWo',
    defaultMessage: 'Status Credits',
    description: 'Column header for status credits in tier progression table',
  })
  const nightsLabel = intl.formatMessage({
    id: 'AnMyb0yxmYSsHKqMA-xj',
    defaultMessage: 'Nights',
    description: 'Column header for nights in tier progression table',
  })
  const learnMoreLabel = intl.formatMessage({
    id: 'vOnWYY10me_ioux5bOqH',
    defaultMessage: 'Learn more',
    description: 'Link text to learn more about the loyalty program in Taste of Gold about tab',
  })

  return (
    <Group direction="vertical" gap={28}>
      <VerticalSpacer gap={16}>
        <VerticalSpacer gap={4}>
          <BodyText variant="large" weight="bold">
            {discoverHeading}
          </BodyText>
          <BodyText variant="medium">
            {unlockDescription}
          </BodyText>
        </VerticalSpacer>
        <Group direction="vertical" gap={12}>
          {tierInfo.map((info) => {
            const tierDisplayName = getLoyaltyTierDisplayName(info.tier)
            const content = (
              <Group direction="horizontal" gap={16} verticalAlign="stretch">
                <TierBadge variant="medium" align="center" className={`tier-${info.tier}`}>
                  {tierDisplayName}
                </TierBadge>
                <TierCardContent>
                  <BodyText variant="medium" weight="bold">{info.title}</BodyText>
                  <BodyText variant="small">{info.description}</BodyText>
                </TierCardContent>
              </Group>
            )

            if (info.highlighted) {
              return (
                <HighlightedTierCard key={info.tier}>
                  <YouAreHereBadge>{youAreHereLabel}</YouAreHereBadge>
                  {content}
                </HighlightedTierCard>
              )
            }

            return <TierCard key={info.tier}>{content}</TierCard>
          })}
        </Group>
      </VerticalSpacer>

      <VerticalSpacer gap={16}>
        <VerticalSpacer gap={4}>
          <BodyText variant="large" weight="bold">
            {movingUpHeading}
          </BodyText>
          <BodyText variant="medium">
            <FormattedMessage
              id="X5OKqQHAw9E3p2S0CFUf"
              defaultMessage="To progress from {bronze} to {silver}, {gold} and {platinum} you’ll need to earn a certain number of Status Credits and count up your Nights."
              description="Description of tier progression requirements in Taste of Gold about tab"
              values={{
                bronze: bronzeName,
                silver: silverName,
                gold: goldName,
                platinum: platinumName,
              }}
            />
          </BodyText>
        </VerticalSpacer>
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>
                <BodyText variant="small" weight="bold" />
              </TableHeaderCell>
              <TableHeaderCell>
                <BodyText variant="small" weight="bold">
                  {statusCreditsLabel}
                </BodyText>
              </TableHeaderCell>
              <TableHeaderCell>
                <BodyText variant="small" weight="bold">
                  {nightsLabel}
                </BodyText>
              </TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            <TableBodyRow>
              <TableBodyCell>
                <BodyText variant="small" weight="bold">{silverName}</BodyText>
              </TableBodyCell>
              <TableBodyCell>
                <BodyText variant="small">{intl.formatNumber(200)}</BodyText>
              </TableBodyCell>
              <TableBodyCell>
                <BodyText variant="small">{intl.formatNumber(10)}</BodyText>
              </TableBodyCell>
            </TableBodyRow>
            <TableBodyRow>
              <TableBodyCell>
                <BodyText variant="small" weight="bold">{goldName}</BodyText>
              </TableBodyCell>
              <TableBodyCell>
                <BodyText variant="small">{intl.formatNumber(600)}</BodyText>
              </TableBodyCell>
              <TableBodyCell>
                <BodyText variant="small">{intl.formatNumber(20)}</BodyText>
              </TableBodyCell>
            </TableBodyRow>
            <TableBodyRow>
              <TableBodyCell>
                <BodyText variant="small" weight="bold">{platinumName}</BodyText>
              </TableBodyCell>
              <TableBodyCell>
                <BodyText variant="small">{intl.formatNumber(1000)}</BodyText>
              </TableBodyCell>
              <TableBodyCell>
                <BodyText variant="small">{intl.formatNumber(30)}</BodyText>
              </TableBodyCell>
            </TableBodyRow>
          </TableBody>
        </Table>
      </VerticalSpacer>
      <div>
        <TextButton kind="secondary" to={`/${LOYALTY_LANDING_PAGE}`} onClick={onNavigateAway}>
          {learnMoreLabel}
        </TextButton>
      </div>
    </Group>
  )
}

export default TasteOfGoldLearnMoreContent
